import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowLeft,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { PhotoUpload } from "../../components/PhotoUpload";
import { DatePicker } from "../../components/DatePicker";
import { ClientBadge, LevelBadge, StatusBadge } from "../../components/Badges";
import { EmptyState } from "../../components/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, apiError, mediaUrl } from "../../lib/api";
import { formatDate, formatINR, initials } from "../../lib/format";
import { cn } from "../../lib/utils";
import {
  BADGE_OPTIONS,
  INVESTMENT_TYPES,
  LEVELS,
  WITHDRAWAL_METHODS,
  WITHDRAWAL_STATUSES,
} from "./constants";

const emptyInvestment = { date: new Date().toISOString(), amount: "", type: "Deposit", note: "" };
const emptyWithdrawal = {
  date: new Date().toISOString(),
  amount: "",
  method: "UPI",
  status: "Pending",
  note: "",
};

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [managers, setManagers] = useState([]);
  const [client, setClient] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [form, setForm] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [invDialog, setInvDialog] = useState(null);
  const [wdDialog, setWdDialog] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/clients/${id}`);
      setClient(data.client);
      setInvestments(data.investments);
      setWithdrawals(data.withdrawals);
      setForm({
        name: data.client.name || "",
        email: data.client.email || "",
        phone: data.client.phone || "",
        address: data.client.address || "",
        photo: data.client.photo || null,
        managerId: data.client.managerId || "none",
        level: data.client.level || "Bronze",
        badges: data.client.badges || [],
        status: data.client.status || "active",
        totalInvested: data.client.totalInvested ?? 0,
        dailyProfitAmount: data.client.dailyProfitAmount ?? 0,
        dailyProfitPercent: data.client.dailyProfitPercent ?? 0,
        overallProfitPercent: data.client.overallProfitPercent ?? 0,
        nextWithdrawalDate: data.client.nextWithdrawalDate || null,
      });
    } catch (error) {
      toast.error(apiError(error, "Could not load the client"));
      navigate("/admin/clients");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    api
      .get("/api/admin/managers")
      .then(({ data }) => setManagers(data.filter((m) => m.id !== id)))
      .catch(() => {});
  }, [load, id]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleBadge = (badge) =>
    setForm((f) => ({
      ...f,
      badges: f.badges.includes(badge) ? f.badges.filter((b) => b !== badge) : [...f.badges, badge],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        photo: form.photo,
        managerId: form.managerId === "none" ? null : form.managerId,
        level: form.level,
        badges: form.badges,
        status: form.status,
        totalInvested: Number(form.totalInvested) || 0,
        dailyProfitAmount: Number(form.dailyProfitAmount) || 0,
        dailyProfitPercent: Number(form.dailyProfitPercent) || 0,
        overallProfitPercent: Number(form.overallProfitPercent) || 0,
        nextWithdrawalDate: form.nextWithdrawalDate,
      };
      if (newPassword) payload.password = newPassword;
      await api.put(`/api/admin/clients/${id}`, payload);
      setNewPassword("");
      toast.success("Client updated");
      await load();
    } catch (error) {
      toast.error(apiError(error, "Could not save changes"));
    } finally {
      setSaving(false);
    }
  };

  const saveInvestment = async () => {
    const body = {
      date: invDialog.date,
      amount: Number(invDialog.amount),
      type: invDialog.type,
      note: invDialog.note || "",
    };
    if (!body.amount || body.amount <= 0) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    try {
      if (invDialog.id) await api.put(`/api/admin/investments/${invDialog.id}`, body);
      else await api.post(`/api/admin/clients/${id}/investments`, body);
      toast.success(invDialog.id ? "Investment entry updated" : "Investment entry added");
      setInvDialog(null);
      await load();
    } catch (error) {
      toast.error(apiError(error, "Could not save the entry"));
    }
  };

  const saveWithdrawal = async () => {
    const body = {
      date: wdDialog.date,
      amount: Number(wdDialog.amount),
      method: wdDialog.method,
      status: wdDialog.status,
      note: wdDialog.note || "",
    };
    if (!body.amount || body.amount <= 0) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    try {
      if (wdDialog.id) await api.put(`/api/admin/withdrawals/${wdDialog.id}`, body);
      else await api.post(`/api/admin/clients/${id}/withdrawals`, body);
      toast.success(wdDialog.id ? "Withdrawal entry updated" : "Withdrawal entry added");
      setWdDialog(null);
      await load();
    } catch (error) {
      toast.error(apiError(error, "Could not save the entry"));
    }
  };

  const runDelete = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    try {
      if (target.kind === "client") {
        await api.delete(`/api/admin/clients/${id}`);
        toast.success("Client deleted");
        navigate("/admin/clients");
        return;
      }
      await api.delete(`/api/admin/${target.kind === "investment" ? "investments" : "withdrawals"}/${target.id}`);
      toast.success("Entry deleted");
      await load();
    } catch (error) {
      toast.error(apiError(error, "Could not delete"));
    }
  };

  if (!form) {
    return (
      <div className="space-y-5" data-testid="page-loading-skeleton">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-10 w-72 rounded-lg" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }

  const photo = mediaUrl(form.photo);

  return (
    <div className="space-y-5">
      <PageHeader
        title={client.name}
        subtitle={client.email}
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/admin/clients" data-testid="admin-client-back">
                <ArrowLeft className="mr-2 h-4 w-4" /> Clients
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmDelete({ kind: "client" })}
              data-testid="admin-client-delete-button"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
            <Button onClick={save} disabled={saving || loading} data-testid="admin-client-save-button">
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save changes
            </Button>
          </>
        }
      />

      <Card className="border-border">
        <CardContent className="flex flex-wrap items-center gap-4 p-4">
          <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-sm font-semibold">
            {photo ? (
              <img src={photo} alt={client.name} className="h-full w-full object-cover" />
            ) : (
              initials(client.name)
            )}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <LevelBadge level={form.level} />
            {form.badges.map((b) => (
              <ClientBadge key={b} label={b} />
            ))}
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                form.status === "active"
                  ? "border-[hsl(var(--profit))]/30 bg-[hsl(var(--profit))]/15 text-[hsl(var(--profit))]"
                  : "border-destructive/30 bg-destructive/15 text-destructive"
              )}
            >
              {form.status === "active" ? "Active" : "Suspended"}
            </span>
          </div>
          <div className="ml-auto flex flex-wrap gap-6 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Invested</p>
              <p className="font-num font-semibold" data-testid="admin-client-header-invested">
                {formatINR(form.totalInvested)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Daily profit</p>
              <p className="font-num font-semibold text-[hsl(var(--profit))]">
                {formatINR(form.dailyProfitAmount)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Next withdrawal</p>
              <p className="font-num font-semibold">{formatDate(form.nextWithdrawalDate)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="profile">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="profile" data-testid="admin-client-tab-profile">
            Profile
          </TabsTrigger>
          <TabsTrigger value="financials" data-testid="admin-client-tab-financials">
            Financials
          </TabsTrigger>
          <TabsTrigger value="investments" data-testid="admin-client-tab-investments">
            Investments ({investments.length})
          </TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="admin-client-tab-withdrawals">
            Withdrawals ({withdrawals.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Contact details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    data-testid="admin-client-edit-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    data-testid="admin-client-edit-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    data-testid="admin-client-edit-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    rows={3}
                    value={form.address}
                    onChange={(e) => set("address", e.target.value)}
                    data-testid="admin-client-edit-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Profile photo</Label>
                  <PhotoUpload value={form.photo} onChange={(v) => set("photo", v)} name={form.name} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">
                  Assignment, level, badges &amp; access
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Manager / upline</Label>
                  <Select value={form.managerId} onValueChange={(v) => set("managerId", v)}>
                    <SelectTrigger data-testid="admin-client-edit-manager">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {managers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} ({m.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Level</Label>
                    <Select value={form.level} onValueChange={(v) => set("level", v)}>
                      <SelectTrigger data-testid="admin-client-edit-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((l) => (
                          <SelectItem key={l} value={l}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Account status</Label>
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger data-testid="admin-client-edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Badges</Label>
                  <div className="flex flex-wrap gap-2">
                    {BADGE_OPTIONS.map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => toggleBadge(b)}
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                          form.badges.includes(b)
                            ? "border-primary bg-primary/15 text-primary"
                            : "border-border text-muted-foreground hover:text-foreground"
                        )}
                        data-testid={`admin-client-edit-badge-${b.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reset password (optional)</Label>
                  <Input
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Leave blank to keep the current password"
                    data-testid="admin-client-edit-password"
                  />
                  <p className="text-xs text-muted-foreground">
                    Must include upper and lower case letters, a number and a special character.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="financials" className="mt-4">
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Financial figures</CardTitle>
              <p className="text-xs text-muted-foreground">
                These values are shown on the client dashboard and are read-only for the client.
              </p>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label>Total invested (INR)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.totalInvested}
                  onChange={(e) => set("totalInvested", e.target.value)}
                  data-testid="admin-client-total-invested"
                />
              </div>
              <div className="space-y-2">
                <Label>Daily profit amount (INR)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.dailyProfitAmount}
                  onChange={(e) => set("dailyProfitAmount", e.target.value)}
                  data-testid="admin-client-daily-profit-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Daily profit %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.dailyProfitPercent}
                  onChange={(e) => set("dailyProfitPercent", e.target.value)}
                  data-testid="admin-client-daily-profit-percent"
                />
              </div>
              <div className="space-y-2">
                <Label>Overall profit %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.overallProfitPercent}
                  onChange={(e) => set("overallProfitPercent", e.target.value)}
                  data-testid="admin-client-overall-profit-percent"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Next expected withdrawal date</Label>
                <DatePicker
                  value={form.nextWithdrawalDate}
                  onChange={(v) => set("nextWithdrawalDate", v)}
                  testId="admin-client-next-withdrawal-date"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <Button onClick={save} disabled={saving || loading} data-testid="admin-client-financials-save">
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save financials
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="investments" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {investments.length} entr{investments.length === 1 ? "y" : "ies"} ·{" "}
              {formatINR(investments.reduce((s, r) => s + Number(r.amount || 0), 0))} recorded
            </p>
            <Button
              onClick={() => setInvDialog({ ...emptyInvestment })}
              data-testid="admin-add-investment-button"
            >
              <Plus className="mr-2 h-4 w-4" /> Add entry
            </Button>
          </div>
          {investments.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="No investment entries"
              description="Add the first entry to build this client's investment ledger."
              testId="admin-investments-empty"
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full min-w-[600px] text-sm" data-testid="admin-investments-table">
                <thead className="surface-2">
                  <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2.5 text-left">Date</th>
                    <th className="px-3 py-2.5 text-left">Type</th>
                    <th className="px-3 py-2.5 text-left">Note</th>
                    <th className="px-3 py-2.5 text-right">Amount</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((row) => (
                    <tr key={row.id} className="border-b border-border last:border-0 hover:bg-accent/40">
                      <td className="px-3 py-3">{formatDate(row.date)}</td>
                      <td className="px-3 py-3">{row.type}</td>
                      <td className="px-3 py-3 text-muted-foreground">{row.note || "—"}</td>
                      <td className="font-num px-3 py-3 text-right">{formatINR(row.amount)}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setInvDialog({ ...row })}
                            data-testid={`admin-edit-investment-${row.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDelete({ kind: "investment", id: row.id })}
                            data-testid={`admin-delete-investment-${row.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="withdrawals" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {withdrawals.length} request{withdrawals.length === 1 ? "" : "s"} ·{" "}
              {formatINR(withdrawals.reduce((s, r) => s + Number(r.amount || 0), 0))} total
            </p>
            <Button
              onClick={() => setWdDialog({ ...emptyWithdrawal })}
              data-testid="admin-add-withdrawal-button"
            >
              <Plus className="mr-2 h-4 w-4" /> Add entry
            </Button>
          </div>
          {withdrawals.length === 0 ? (
            <EmptyState
              icon={ArrowDownToLine}
              title="No withdrawal entries"
              description="Record a withdrawal request to track its approval status."
              testId="admin-withdrawals-empty"
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full min-w-[640px] text-sm" data-testid="admin-withdrawals-table">
                <thead className="surface-2">
                  <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2.5 text-left">Date</th>
                    <th className="px-3 py-2.5 text-left">Method</th>
                    <th className="px-3 py-2.5 text-left">Status</th>
                    <th className="px-3 py-2.5 text-left">Note</th>
                    <th className="px-3 py-2.5 text-right">Amount</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((row) => (
                    <tr key={row.id} className="border-b border-border last:border-0 hover:bg-accent/40">
                      <td className="px-3 py-3">{formatDate(row.date)}</td>
                      <td className="px-3 py-3">{row.method}</td>
                      <td className="px-3 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">{row.note || "—"}</td>
                      <td className="font-num px-3 py-3 text-right">{formatINR(row.amount)}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setWdDialog({ ...row })}
                            data-testid={`admin-edit-withdrawal-${row.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDelete({ kind: "withdrawal", id: row.id })}
                            data-testid={`admin-delete-withdrawal-${row.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(invDialog)} onOpenChange={(o) => !o && setInvDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{invDialog?.id ? "Edit investment entry" : "Add investment entry"}</DialogTitle>
          </DialogHeader>
          {invDialog ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <DatePicker
                  value={invDialog.date}
                  onChange={(v) => setInvDialog((d) => ({ ...d, date: v || new Date().toISOString() }))}
                  clearable={false}
                  testId="investment-dialog-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount (INR)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invDialog.amount}
                  onChange={(e) => setInvDialog((d) => ({ ...d, amount: e.target.value }))}
                  data-testid="investment-dialog-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={invDialog.type}
                  onValueChange={(v) => setInvDialog((d) => ({ ...d, type: v }))}
                >
                  <SelectTrigger data-testid="investment-dialog-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVESTMENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note (optional)</Label>
                <Input
                  value={invDialog.note || ""}
                  onChange={(e) => setInvDialog((d) => ({ ...d, note: e.target.value }))}
                  data-testid="investment-dialog-note"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInvDialog(null)}>
              Cancel
            </Button>
            <Button onClick={saveInvestment} data-testid="investment-dialog-save">
              Save entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(wdDialog)} onOpenChange={(o) => !o && setWdDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{wdDialog?.id ? "Edit withdrawal entry" : "Add withdrawal entry"}</DialogTitle>
          </DialogHeader>
          {wdDialog ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <DatePicker
                  value={wdDialog.date}
                  onChange={(v) => setWdDialog((d) => ({ ...d, date: v || new Date().toISOString() }))}
                  clearable={false}
                  testId="withdrawal-dialog-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount (INR)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={wdDialog.amount}
                  onChange={(e) => setWdDialog((d) => ({ ...d, amount: e.target.value }))}
                  data-testid="withdrawal-dialog-amount"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Method</Label>
                  <Select
                    value={wdDialog.method}
                    onValueChange={(v) => setWdDialog((d) => ({ ...d, method: v }))}
                  >
                    <SelectTrigger data-testid="withdrawal-dialog-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WITHDRAWAL_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={wdDialog.status}
                    onValueChange={(v) => setWdDialog((d) => ({ ...d, status: v }))}
                  >
                    <SelectTrigger data-testid="withdrawal-dialog-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WITHDRAWAL_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note (optional)</Label>
                <Input
                  value={wdDialog.note || ""}
                  onChange={(e) => setWdDialog((d) => ({ ...d, note: e.target.value }))}
                  data-testid="withdrawal-dialog-note"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWdDialog(null)}>
              Cancel
            </Button>
            <Button onClick={saveWithdrawal} data-testid="withdrawal-dialog-save">
              Save entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.kind === "client" ? "Delete this client?" : "Delete this entry?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.kind === "client"
                ? "The client account and all their investment and withdrawal records will be permanently removed. This cannot be undone."
                : "This ledger entry will be permanently removed. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
